import React, {useCallback, useEffect, useRef, useState} from 'react';
import styles from './chat.module.scss';
import altButtonStyles from './../../components/button/alt_button.module.scss';
import {Form, useSearchParams} from 'react-router-dom';
import {notificationSelector} from '../../store/slices/notifications_slice';
import {useAppSelector} from '../../store/store';
import {parseIntOrFallback} from '../../support/parse_int';
import {AltButton} from '../../components/button/alt_button.tsx';
import {Spacer} from '../../components/utility/spacer.tsx';
import {classNames} from '../../support/classnames.ts';
import navStyles from '../../components/navigation/navigation.module.scss';
import {__} from '../../support/lang/translations.ts';
import {useClickOutside} from '../../support/use_click_outside.ts';
import {useIsNavigatingOrPending} from '../../support/use_is_navigating_or_pending.ts';

interface OwnProps {
    messageCount?: number;
    name: string;
}

export function MessageForm({messageCount, name}: OwnProps) {
    const [params, setSearchParams] = useSearchParams();
    const notificationId = parseIntOrFallback(params.get('notificationId'), null);
    const [message, setMessage] = useState('');

    const notification = useAppSelector((state) => notificationSelector(state, notificationId));
    const isSubmitting = useIsNavigatingOrPending((state) => state.chat.status);

    const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const [file, setFile] = useState<string | null>(null);
    const [rawFile, setRawFile] = useState<File | null>(null);
    const isImage = rawFile && rawFile.type.includes('image');

    const dropupRef = React.useRef<HTMLDivElement | null>(null);
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [textareaHeight, setTextareaHeight] = useState<number | null>(null);

    const closePopupCallback = useCallback(() => {
        setIsAttachMenuOpen(false);
    }, [setIsAttachMenuOpen]);
    useClickOutside(dropupRef, closePopupCallback);

    useEffect(() => {
        if (inputRef.current?.value && file === null) {
            inputRef.current.value = '';
        }
    }, [file]);

    useEffect(() => {
        setTextareaHeight(null);
    }, [message]);

    useEffect(() => {
        setTextareaHeight(textareaRef.current?.scrollHeight ?? 20);
    }, [textareaHeight]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) {
            return;
        }
        const file = files[0];

        setRawFile(file);
        setFile(URL.createObjectURL(file));
        setIsAttachMenuOpen(false);
    };

    const handleRemoveFile = () => {
        if (inputRef.current?.value) {
            inputRef.current.value = '';
        }
        setRawFile(null);
        setFile(null);
    };

    function handleFileButtonClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        if (!inputRef || !inputRef.current) return;

        inputRef.current.click();
    }

    const onSendLocationClick = async () => {
        setIsLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (position: GeolocationPosition) => {
                const baseUrl = 'https://www.google.com/maps/place/';
                let googleMapsUrl: string = baseUrl;
                googleMapsUrl = googleMapsUrl + position.coords.latitude + ',' + position.coords.longitude;
                if (googleMapsUrl !== baseUrl) {
                    try {
                        setMessage(googleMapsUrl);
                    } catch (errors) {
                        console.error(errors);
                    } finally {
                        setIsLoading(false);
                        setIsAttachMenuOpen(false);
                    }
                }
            },
            async (error) => {
                console.error(error);
                setIsLoading(false);
            },
        );
    };

    const onSubmit = () => {
        setMessage('');
        setFile(null);
        setRawFile(null);
    };

    return (
        <div className={styles.chat_form_wrapper}>
            {messageCount == null ||
                (messageCount >= 0 && messageCount < 10 && (
                    <div className={styles.chat_placeholder}>
                        <em>{__('chat.placeholder', {name: name})}</em>
                        <i className="fa-light fa-info-circle" />
                    </div>
                ))}
            <Form
                className={classNames(styles.chat_form, {
                    [styles.loading]: isLoading,
                    [styles.border]: messageCount === 0,
                })}
                method="post"
                onSubmit={() => onSubmit()}
                encType="multipart/form-data"
            >
                <div
                    ref={dropupRef}
                    className={classNames(styles.message_attach_dropup, {
                        [styles.open]: isAttachMenuOpen,
                    })}
                >
                    <button type="button" className={navStyles.menu__item} onClick={handleFileButtonClick}>
                        <span className={navStyles.item__icon}>
                            <i className="fa-light fa-file-alt" />
                        </span>
                        <span className={navStyles.item__text}>{__('chat.attach.file')}</span>
                    </button>
                    <button type="button" className={navStyles.menu__item} onClick={() => onSendLocationClick()}>
                        <span className={navStyles.item__icon}>
                            <i className="fa-light fa-location" />
                        </span>
                        <span className={navStyles.item__text}>{__('chat.attach.location')}</span>
                    </button>
                </div>

                <input name="file" ref={inputRef} type="file" hidden onChange={handleFileUpload} />

                <AltButton
                    type="button"
                    className={classNames(altButtonStyles.icon, altButtonStyles.no_border)}
                    onClick={() => setIsAttachMenuOpen(!isAttachMenuOpen)}
                >
                    <i className="fa-light fa-plus" />
                </AltButton>
                <Spacer />
                <div className={styles.chat_input_container}>
                    {notification && (
                        <div className={styles.message_persisted_notification}>
                            {notification.message}
                            <i
                                className="fa-light fa-close"
                                onClick={() => {
                                    setSearchParams(
                                        (prev) => {
                                            prev.delete('notificationId');
                                            return prev;
                                        },
                                        {replace: true},
                                    );
                                }}
                            />
                        </div>
                    )}
                    {file && (
                        <div className={styles.message_file}>
                            {isImage ? (
                                <img alt="preview image" src={file} />
                            ) : (
                                <div className={styles.file_icon}>
                                    <i className="fa-solid fa-file" />
                                </div>
                            )}
                            {!isImage && <div className={styles.file_name}>{rawFile?.name}</div>}
                            <i
                                className="fa-light fa-close"
                                onClick={() => {
                                    handleRemoveFile();
                                }}
                            />
                        </div>
                    )}
                    <textarea
                        style={textareaHeight ? {height: textareaHeight} : {}}
                        className={styles.input}
                        ref={textareaRef}
                        autoFocus={false}
                        rows={1}
                        name="message"
                        placeholder="Start met een bericht te typen"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        autoComplete="off"
                    />
                    {notificationId != null && (
                        <input type="hidden" name="notification_id" value={notificationId?.toString() ?? ''} />
                    )}
                </div>
                <Spacer />
                <AltButton
                    type="submit"
                    loading={isSubmitting}
                    className={altButtonStyles.icon}
                    disabled={message?.length === 0}
                >
                    <i className={isLoading ? 'fa-light fa-refresh' : 'fa-light fa-paper-plane'} />
                </AltButton>
            </Form>
        </div>
    );
}
